import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { useContext, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
	deleteRelatedPerson,
	getPersonRelatedPeople,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/apis/useRelatedPeople.ts';
import { PersonRelationType, RelatedPersonRes } from '@dealstack/shared/lib/generated';

export const useRelatedPeople = () => {
	const { selectedEntityId, selectedEntityData } = useContext(CapManageControllerContext);
	const [editRelation, setEditRelation] = useState<{
		relatedPerson: RelatedPersonRes;
		relationType: PersonRelationType;
	} | null>(null);
	const [deleteRelation, setDeleteRelation] = useState<{
		relatedPerson: RelatedPersonRes;
		relationType: PersonRelationType;
	} | null>(null);
	const [addPeopleToRelation, setAddPeopleToRelation] = useState<PersonRelationType | null>(null);

	const {
		data: relatedPeople,
		isLoading: relatedPeopleLoading,
		isFetching: relatedPeopleFetching,
		refetch: refetchRelatedPeople,
	} = useQuery({
		queryKey: ['relatedPeople', selectedEntityId],
		queryFn: () => getPersonRelatedPeople(selectedEntityId),
		enabled: !!selectedEntityId,
	});

	const deleteRelatedPersonMutation = useMutation({
		mutationKey: ['deleteRelatedPerson', { personId: selectedEntityId }],
		mutationFn: deleteRelatedPerson,
	});

	const removePersonRelation = () => {
		if (!deleteRelation) {
			return;
		}
		const { relatedPerson, relationType } = deleteRelation;

		deleteRelatedPersonMutation.mutate(
			{
				personId: selectedEntityId,
				body: {
					relatedPersonId: relatedPerson.id,
					relationType,
				},
			},
			{
				onSuccess: () => {
					void refetchRelatedPeople();
					setDeleteRelation(null);
				},
			}
		);
	};

	const editPersonRelation = (
		relatedPerson: RelatedPersonRes,
		relationType: PersonRelationType
	) => {
		setEditRelation({
			relatedPerson,
			relationType,
		});
	};

	return {
		pivs: relatedPeople?.[PersonRelationType.PIV] ?? [],
		representatives: relatedPeople?.[PersonRelationType.REPRESENTATIVE] ?? [],
		ubos: relatedPeople?.[PersonRelationType.UBO] ?? [],
		pscs: relatedPeople?.[PersonRelationType.PSC] ?? [],
		directors: relatedPeople?.[PersonRelationType.DIRECTOR] ?? [],
		trustees: relatedPeople?.[PersonRelationType.TRUSTEE] ?? [],
		settlorsOrGrantors: relatedPeople?.[PersonRelationType.SETTLER_OR_GRANTOR] ?? [],
		protectorsOrEnforcers: relatedPeople?.[PersonRelationType.PROTECTOR_OR_ENFORCER] ?? [],
		beneficiaries: relatedPeople?.[PersonRelationType.BENEFICIARY] ?? [],
		otherControllingParties: relatedPeople?.[PersonRelationType.OTHER] ?? [],
		isRelatedPeopleLoading: relatedPeopleLoading || relatedPeopleFetching,
		removePersonRelation,
		editPersonRelation,
		refetchRelatedPeople,
		editRelation,
		setEditRelation,
		deleteRelation,
		setDeleteRelation,
		deleteRelationLoading: deleteRelatedPersonMutation.isPending,
		deleteRelationSuccess: deleteRelatedPersonMutation.isSuccess,
		selectedEntityType: selectedEntityData?.type,
		addPeopleToRelation,
		setAddPeopleToRelation,
	};
};
